import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Surface, Badge, Button, PageHeader, Alert } from '../../ui/components';
import { Upload, Info, Check, Briefcase, Wallet, CreditCard, Home } from 'lucide-react';
import { AppLayout } from '../../components/layout';
import { BORROWER_NAV } from '../../config/navigation';
import { supabase } from '../../lib/supabase';
import FloatingOwenAssistant from '../../components/common/FloatingOwenAssistant';
import FloatingKnowledge from '../../components/common/FloatingKnowledge';

const documentCategories = [
  { id: 'income', label: 'Income Documents', icon: Briefcase },
  { id: 'assets', label: 'Asset Documents', icon: Wallet },
  { id: 'identity', label: 'Identity Documents', icon: CreditCard },
  { id: 'property', label: 'Property Documents', icon: Home },
];

const requiredDocuments = [
  { id: 'pay_stubs', name: 'Pay Stubs (Last 2)', category: 'income', required: true },
  { id: 'w2', name: 'W2 Forms (2 Years)', category: 'income', required: true },
  { id: 'tax_returns', name: 'Tax Returns (2 Years)', category: 'income', required: true },
  { id: 'bank_statements', name: 'Bank Statements (2 Months)', category: 'assets', required: true },
  { id: 'photo_id', name: 'Photo ID', category: 'identity', required: true },
  { id: 'purchase_contract', name: 'Purchase Contract', category: 'property', required: false },
];

const statusColors = {
  pending: 'neutral',
  uploaded: 'primary',
  reviewing: 'warning',
  approved: 'success',
  rejected: 'danger',
};

const BorrowerDocuments = () => {
  const navigate = useNavigate();
  const [borrower, setBorrower] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Check for demo session first
      const demoSession = localStorage.getItem('lk_demo_session');
      if (demoSession) {
        const demoUser = JSON.parse(demoSession);
        setBorrower({
          id: demoUser.id,
          email: demoUser.email,
          firstName: demoUser.user_metadata?.firstName || 'Demo',
          lastName: demoUser.user_metadata?.lastName || 'Borrower',
        });
        await fetchDocuments(demoUser.id);
        return;
      }

      // Otherwise check Supabase session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        navigate('/borrower/login');
        return;
      }

      setBorrower({
        id: session.user.id,
        email: session.user.email,
        firstName: session.user.user_metadata?.firstName || session.user.email.split('@')[0],
        lastName: session.user.user_metadata?.lastName || '',
      });

      await fetchDocuments(session.user.id);
    } catch (error) {
      console.error('Auth error:', error);
      navigate('/borrower/login');
    }
  };

  const DEMO_DOCUMENTS = [
    { id: 'doc-1', user_id: 'demo-borrower-123', document_type: 'pay_stubs', file_name: 'PayStub_Jan2026.pdf', file_size: 245000, status: 'approved', created_at: new Date(Date.now() - 2 * 86400000).toISOString() },
    { id: 'doc-2', user_id: 'demo-borrower-123', document_type: 'w2', file_name: 'W2_2025.pdf', file_size: 180000, status: 'uploaded', created_at: new Date(Date.now() - 86400000).toISOString() },
  ];

  const fetchDocuments = async (userId) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if ((!data || data.length === 0) && userId === 'demo-borrower-123') {
        setDocuments(DEMO_DOCUMENTS);
      } else {
        setDocuments(data || []);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      if (userId === 'demo-borrower-123') {
        setDocuments(DEMO_DOCUMENTS);
      } else {
        setDocuments([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    localStorage.removeItem('lk_demo_session');
    await supabase.auth.signOut();
    navigate('/');
  };

  const handleFileUpload = async (docType, event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      // In production, upload to Supabase Storage
      // For demo, just create a document record
      const { error } = await supabase
        .from('documents')
        .insert({
          user_id: borrower.id,
          document_type: docType,
          file_name: file.name,
          file_size: file.size,
          status: 'uploaded',
        });

      if (error) throw error;

      await fetchDocuments(borrower.id);
      alert('Document uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const getDocumentStatus = (docId) => {
    const doc = documents.find(d => d.document_type === docId);
    return doc?.status || 'pending';
  };

  const getDocumentCount = (docId) => {
    return documents.filter(d => d.document_type === docId).length;
  };

  const calculateProgress = () => {
    const required = requiredDocuments.filter(d => d.required);
    const uploaded = required.filter(d => getDocumentStatus(d.id) !== 'pending');
    return required.length > 0 ? Math.round((uploaded.length / required.length) * 100) : 0;
  };

  const filteredDocuments = selectedCategory === 'all' 
    ? requiredDocuments 
    : requiredDocuments.filter(d => d.category === selectedCategory);

  if (!borrower) return null;

  const progress = calculateProgress();

  return (
    <AppLayout
      role="borrower"
      navItems={BORROWER_NAV}
      user={{
        name: `${borrower.firstName} ${borrower.lastName}`,
        email: borrower.email,
        role: 'borrower',
      }}
      onLogout={handleLogout}
    >
      <div>
        <PageHeader
          title="Documents"
          description="Upload and manage your application documents"
        />

        {/* Progress Card */}
        <Surface padding="var(--lk-space-5)" style={{ marginBottom: 'var(--lk-space-4)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--lk-space-3)' }}>
            <div style={{ fontWeight: 600 }}>Document Completion</div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-quaternary)' }}>Required Documents</div>
              <div style={{ fontSize: 'var(--lk-text-xl)', fontWeight: 700 }}>
                {requiredDocuments.filter(d => d.required && getDocumentStatus(d.id) !== 'pending').length}
                <span style={{ color: 'var(--lk-text-quaternary)' }}> / </span>
                {requiredDocuments.filter(d => d.required).length}
              </div>
            </div>
          </div>
          <div style={{ height: 12, borderRadius: 6, backgroundColor: 'var(--lk-gray-100)' }}>
            <div style={{
              height: '100%',
              width: `${progress}%`,
              borderRadius: 6,
              backgroundColor: progress === 100 ? 'var(--lk-success-500)' : progress >= 50 ? 'var(--lk-primary-500)' : 'var(--lk-warning-500)',
              transition: 'width 0.6s ease',
            }} />
          </div>
        </Surface>

        {/* Info Alert */}
        <Alert variant="info" icon={Info} style={{ marginBottom: 'var(--lk-space-4)' }}>
          <strong>Important:</strong> Upload clear, legible copies of all required documents. Accepted formats: PDF, JPG, PNG.
        </Alert>

        {/* Category Filter */}
        <Surface padding="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-4)' }}>
          <div style={{ display: 'flex', gap: 'var(--lk-space-2)', flexWrap: 'wrap' }}>
            <Button
              size="sm"
              variant={selectedCategory === 'all' ? 'primary' : 'secondary'}
              onClick={() => setSelectedCategory('all')}
            >
              All Documents
            </Button>
            {documentCategories.map(cat => {
              const CatIcon = cat.icon;
              return (
                <Button
                  key={cat.id}
                  size="sm"
                  variant={selectedCategory === cat.id ? 'primary' : 'secondary'}
                  icon={CatIcon}
                  onClick={() => setSelectedCategory(cat.id)}
                >
                  {cat.label}
                </Button>
              );
            })}
          </div>
        </Surface>

        {/* Documents Table */}
        <Surface padding="0" style={{ overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="lk-table">
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Category</th>
                  <th>Required</th>
                  <th>Status</th>
                  <th>Uploaded Files</th>
                  <th style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: 'var(--lk-space-8)' }}>
                      <div className="lk-spinner lk-spinner-sm" style={{ display: 'inline-block', marginRight: 8 }} />
                      Loading documents...
                    </td>
                  </tr>
                ) : filteredDocuments.length > 0 ? (
                  filteredDocuments.map((doc) => {
                    const status = getDocumentStatus(doc.id);
                    const count = getDocumentCount(doc.id);

                    return (
                      <tr key={doc.id}>
                        <td style={{ fontWeight: 500 }}>{doc.name}</td>
                        <td>
                          <Badge variant="neutral">
                            {documentCategories.find(c => c.id === doc.category)?.label}
                          </Badge>
                        </td>
                        <td>
                          {doc.required ? (
                            <Badge variant="danger">Required</Badge>
                          ) : (
                            <Badge variant="neutral">Optional</Badge>
                          )}
                        </td>
                        <td>
                          <Badge variant={statusColors[status] || 'neutral'}>
                            {status === 'pending' ? 'Not Uploaded' : status}
                          </Badge>
                        </td>
                        <td>
                          {count > 0 ? (
                            <span style={{ color: 'var(--lk-success-600)', display: 'flex', alignItems: 'center', gap: 4 }}>
                              <Check size={14} />
                              {count} file{count > 1 ? 's' : ''}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--lk-text-quaternary)' }}>No files</span>
                          )}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => handleFileUpload(doc.id, e)}
                            disabled={uploading}
                            style={{ display: 'none' }}
                            id={`upload-${doc.id}`}
                          />
                          <Button
                            size="sm"
                            variant="primary"
                            icon={Upload}
                            onClick={() => document.getElementById(`upload-${doc.id}`).click()}
                            disabled={uploading}
                          >
                            Upload
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', color: 'var(--lk-text-quaternary)', padding: 'var(--lk-space-8)' }}>
                      No documents in this category
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Surface>
      </div>
      <FloatingOwenAssistant context="borrower-documents" />
      <FloatingKnowledge context="documents" />
    </AppLayout>
  );
};

export default BorrowerDocuments;
